import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApolloClient } from '@apollo/client';
import { get } from 'lodash';
import { message, Switch } from 'antd';
import { LuPencil, LuX } from 'react-icons/lu';
import { motion, AnimatePresence } from 'motion/react';
import { Editor } from '@hugerte/hugerte-react';
import {
	downloadFromS3Signed,
	uploadImageToS3FromHugeRTE,
} from 'src/_shared/services/utils.js';
import {
	QUERY_INTERNAL_MOBILITY,
	CREATE_INTERNAL_MOBILITY,
	UPDATE_INTERNAL_MOBILITY,
} from './graphql.js';
import { darkenHex, accentRgba } from './utils.js';

const SECTIONS = [
	{ key: 'contentFirst', label: 'Section 1' },
	{ key: 'contentMiddle', label: 'Section 2' },
	{ key: 'contentLast', label: 'Section 3' },
];

const EDITOR_INIT = {
	height: 350,
	menubar: false,
	branding: false,
	plugins: ['image', 'code', 'media', 'link', 'lists'],
	toolbar:
		'undo redo | styles | fontfamily | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | media link image',
	automatic_uploads: true,
	file_picker_types: 'image',
	placeholder: 'Start typing here to add Internal Mobility Resources...',
	content_style: `img { max-width: 100%; } .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: rgba(118, 118, 118, 1); opacity: 1; }`,
};

function decodeHtml(html) {
	const txt = document.createElement('textarea');
	txt.innerHTML = html;
	return txt.value;
}

async function refreshSignedUrls(rawHtml) {
	const html = decodeHtml(rawHtml || '');
	if (!html || !html.includes('img') || !html.includes('erin-avatars.')) {
		return html;
	}
	try {
		const srcRegex = /src=['"]([^'"]*erin-avatars[^'"]*)['"]/g;
		let result = html;
		const matches = [...html.matchAll(srcRegex)];
		for (const match of matches) {
			const fullUrl = match[1];
			const parts = fullUrl.split('.com/');
			if (parts.length < 2) continue;
			const key = decodeURIComponent(parts[1].split('?')[0]);
			const freshUrl = await downloadFromS3Signed(key);
			if (freshUrl) {
				result = result.replace(match[0], `src='${freshUrl}'`);
			}
		}
		return result;
	} catch (error) {
		console.error('Error refreshing signed URLs:', error);
		return html;
	}
}


function EditorModal({ sectionLabel, content, onSave, onClose, accentColor, companyId }) {
	const [value, setValue] = useState(content || '');
	const [saving, setSaving] = useState(false);

	const uploadImagePromise = useCallback(
		(blobInfo, progress) => {
			return new Promise(async (resolve, reject) => {
				try {
					const image = blobInfo.base64();
					const file = blobInfo.blob();
					if (typeof progress === 'function') progress(10);
					const uploadFile = await uploadImageToS3FromHugeRTE(
						image,
						file,
						`internal-mobility/${companyId}/${Date.now()}-${file.name}`
					);
					if (typeof progress === 'function') progress(50);
					const link = await downloadFromS3Signed(
						uploadFile.key,
						uploadFile.bucket
					);
					if (typeof progress === 'function') progress(100);
					resolve(link);
				} catch (error) {
					reject(error.message || 'Image upload failed');
					message.error('Image upload failed');
				}
			});
		},
		[companyId]
	);

	const handleSave = async () => {
		setSaving(true);
		try {
			await onSave(value);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			onClick={onClose}
		>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className="fixed inset-0"
				style={{
					background: 'rgba(15,23,42,0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.95 }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
				className="relative flex w-full flex-col overflow-hidden rounded-2xl bg-white"
				style={{
					maxWidth: 720,
					maxHeight: '90vh',
					boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div
					className="flex items-center justify-between p-6"
					style={{ paddingBottom: 0 }}
				>
					<h2
						style={{
							color: '#1e293b',
							fontSize: 18,
							fontWeight: 600,
							margin: 0,
						}}
					>
						Edit {sectionLabel}
					</h2>
					<button
						type="button"
						onClick={onClose}
						style={{
							width: 32,
							height: 32,
							borderRadius: 12,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							color: '#475569',
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = '#f1f5f9';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = 'none';
						}}
					>
						<LuX size={14} />
					</button>
				</div>

				<div
					className="overflow-y-auto p-6"
					style={{
						scrollbarWidth: 'thin',
						scrollbarColor: '#cbd5e1 transparent',
					}}
				>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						<Editor
							init={{
								...EDITOR_INIT,
								images_upload_handler: uploadImagePromise,
							}}
							value={value}
							onEditorChange={setValue}
						/>

						<div className="flex gap-3 pt-2">
							<button
								type="button"
								onClick={onClose}
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									background: '#f1f5f9',
									color: '#475569',
									border: 'none',
									cursor: 'pointer',
									transition: 'background 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = '#e2e8f0';
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = '#f1f5f9';
								}}
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleSave}
								disabled={saving}
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									color: '#fff',
									background: accentColor,
									border: 'none',
									cursor: saving ? 'wait' : 'pointer',
									boxShadow: `0 1px 3px ${accentRgba(accentColor, 0.3)}`,
									transition: 'all 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = darkenHex(accentColor);
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = accentColor;
								}}
							>
								{saving ? 'Saving...' : 'Save'}
							</button>
						</div>
					</div>
				</div>
			</motion.div>
		</div>
	);
}

function InternalMobilityResources({ currentUser, accentColor, onSettingsChange }) {
	const client = useApolloClient();
	const companyId = currentUser?.companyId;
	const isAdmin =
		currentUser?.displayAs !== 'employee' &&
		(currentUser?.role === 'admin' ||
			currentUser?.role === 'superAdmin' ||
			currentUser?.role === 'supportAdmin');

	const [mobilityData, setMobilityData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [editingSection, setEditingSection] = useState(null);

	useEffect(() => {
		if (!companyId) return;
		let cancelled = false;

		const fetch = async () => {
			try {
				const { data } = await client.query({
					query: QUERY_INTERNAL_MOBILITY,
					variables: { companyId, first: 1 },
					fetchPolicy: 'network-only',
				});
				const items = get(
					data,
					'queryInternalMobilityByCompanyIdIndex.items',
					[]
				);
				if (!cancelled) {
					const raw = items[0] || null;
					if (raw) {
						const record = {
							...raw,
							contentFirst: await refreshSignedUrls(raw.contentFirst || ''),
							contentMiddle: await refreshSignedUrls(raw.contentMiddle || ''),
							contentLast: await refreshSignedUrls(raw.contentLast || ''),
						};
						setMobilityData(record);
						onSettingsChange?.({
							hideOpenToNewRoles: !!raw.hideOpenToNewRoles,
							hideResources: !!raw.hideResources,
						});
					} else {
						setMobilityData(null);
					}
				}
			} catch (error) {
				console.error('Error fetching internal mobility:', error);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetch();
		return () => {
			cancelled = true;
		};
	}, [client, companyId]);

	const handleSave = useCallback(
		async (sectionKey, content) => {
			try {
				if (mobilityData?.id) {
					const result = await client.mutate({
						mutation: UPDATE_INTERNAL_MOBILITY,
						variables: {
							input: {
								id: mobilityData.id,
								companyId,
								[sectionKey]: content,
							},
						},
					});
					const updated = get(
						result,
						'data.updateInternalMobility'
					);
					if (updated) {
						setMobilityData((prev) => ({
							...prev,
							...updated,
							[sectionKey]: content,
						}));
					}
				} else {
					const result = await client.mutate({
						mutation: CREATE_INTERNAL_MOBILITY,
						variables: {
							input: {
								companyId,
								[sectionKey]: content,
							},
						},
					});
					const created = get(
						result,
						'data.createInternalMobility'
					);
					if (created) {
						setMobilityData({
							...created,
							[sectionKey]: content,
						});
					}
				}
				message.success('Content saved');
				setEditingSection(null);
			} catch (error) {
				console.error('Error saving internal mobility:', error);
				message.error('Failed to save content');
			}
		},
		[client, companyId, mobilityData?.id]
	);

	const handleToggle = useCallback(
		async (field, value) => {
			const prev = mobilityData?.[field];
			setMobilityData((p) => (p ? { ...p, [field]: value } : p));
			onSettingsChange?.({ [field]: value });
			try {
				if (mobilityData?.id) {
					await client.mutate({
						mutation: UPDATE_INTERNAL_MOBILITY,
						variables: {
							input: {
								id: mobilityData.id,
								companyId,
								[field]: value,
							},
						},
					});
				} else {
					const result = await client.mutate({
						mutation: CREATE_INTERNAL_MOBILITY,
						variables: {
							input: {
								companyId,
								[field]: value,
							},
						},
					});
					const created = get(result, 'data.createInternalMobility');
					if (created) setMobilityData((p) => ({ ...p, ...created }));
				}
			} catch (error) {
				console.error('Error toggling setting:', error);
				message.error('Failed to update setting');
				setMobilityData((p) => (p ? { ...p, [field]: prev } : p));
				onSettingsChange?.({ [field]: prev });
			}
		},
		[client, companyId, mobilityData, onSettingsChange]
	);

	if (loading) return null;

	const hideResources = !!mobilityData?.hideResources;
	const hideOpenToNewRoles = !!mobilityData?.hideOpenToNewRoles;

	const hasAnyContent =
		mobilityData?.contentFirst ||
		mobilityData?.contentMiddle ||
		mobilityData?.contentLast;

	if (!isAdmin && (hideResources || !hasAnyContent)) return null;

	return (
		<section
			className="career-hub-section rounded-2xl bg-white"
			style={{
				animationDelay: '0.05s',
				border: '1px solid #f1f5f9',
				padding: 20,
				boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
			}}
		>
			<style>{`.career-hub-resource-section:hover .career-hub-resource-edit { opacity: 1 !important; }`}</style>
			<div
				className="flex flex-wrap items-center justify-between gap-3"
				style={{ marginBottom: 16 }}
			>
				<h2
					style={{
						fontSize: 16,
						fontWeight: 600,
						color: '#1e293b',
						margin: 0,
					}}
				>
					Internal Mobility Resources
				</h2>
				{isAdmin && (
					<div className="flex items-center" style={{ gap: 16 }}>
						<div className="flex items-center" style={{ gap: 8 }}>
							<span
								style={{
									fontSize: 12,
									color: '#475569',
									fontWeight: 500,
									lineHeight: 1,
								}}
							>
								Hide Resources
							</span>
							<Switch
								size="small"
								checked={hideResources}
								onChange={(val) => handleToggle('hideResources', val)}
								style={hideResources ? { background: accentColor } : undefined}
							/>
						</div>
						<div className="flex items-center" style={{ gap: 8 }}>
							<span
								style={{
									fontSize: 12,
									color: '#475569',
									fontWeight: 500,
									lineHeight: 1,
								}}
							>
								Hide Open to New Roles
							</span>
							<Switch
								size="small"
								checked={hideOpenToNewRoles}
								onChange={(val) => handleToggle('hideOpenToNewRoles', val)}
								style={hideOpenToNewRoles ? { background: accentColor } : undefined}
							/>
						</div>
					</div>
				)}
			</div>

			<div className="flex flex-col gap-4 lg:flex-row">
				{SECTIONS.filter(({ key }) => isAdmin || mobilityData?.[key]).map(({ key, label }) => (
					<div
						key={key}
						className="career-hub-resource-section group relative flex-1 rounded-xl"
						style={{
							padding: 16,
							border: '1px solid #f1f5f9',
							minHeight: 80,
							maxHeight: 200,
							overflowY: 'auto',
							background: '#fff',
						}}
					>
						{isAdmin && (
							<button
								type="button"
								onClick={() => setEditingSection({ key, label })}
								className="career-hub-resource-edit absolute"
								style={{
									top: 8,
									right: 8,
									width: 28,
									height: 28,
									borderRadius: 8,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									padding: 0,
									border: 'none',
									background: 'rgba(255,255,255,0.9)',
									color: '#475569',
									cursor: 'pointer',
									opacity: 0,
									transition:
										'opacity 0.15s, color 0.15s, background 0.15s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = accentRgba(
										accentColor,
										0.1
									);
									e.currentTarget.style.color = accentColor;
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background =
										'rgba(255,255,255,0.9)';
									e.currentTarget.style.color = '#475569';
								}}
							>
								<LuPencil size={13} />
							</button>
						)}
						{mobilityData?.[key] ? (
							<div
								style={{
									fontSize: 13,
									lineHeight: 1.6,
									color: '#334155',
								}}
								dangerouslySetInnerHTML={{
									__html: mobilityData[key],
								}}
							/>
						) : (
							<p
								style={{
									fontSize: 13,
									color: '#475569',
									margin: 0,
									textAlign: 'center',
									padding: '16px 0',
								}}
							>
								{isAdmin ? 'Click edit to add content' : 'No content yet'}
							</p>
						)}
					</div>
				))}
			</div>

			{createPortal(
				<AnimatePresence>
					{editingSection && (
						<EditorModal
							sectionLabel={editingSection.label}
							content={mobilityData?.[editingSection.key] || ''}
							onSave={(content) =>
								handleSave(editingSection.key, content)
							}
							onClose={() => setEditingSection(null)}
							accentColor={accentColor}
							companyId={companyId}
						/>
					)}
				</AnimatePresence>,
				document.body
			)}
		</section>
	);
}

export default InternalMobilityResources;
